import { type StartCommunityOfPractice } from '../models/StartCommunityOfPractice'
import { useEffect, useState } from 'react'
import { type UpdateStartCommunityOfPracticeFormValues } from '../pages/pageComponents/planning/priorityActivities/PriorityOne/StartCommunityOfPracticeModal'
import { GetStartCommunityOfPractices, LockActivity, UnlockActivity } from '../services/AnnualPlanService'
import { handleApiResponse } from '../utils/apiHelpers'

interface UseStartCommunityOfPracticesType {
  startCommunityOfPractices: StartCommunityOfPractice[]
  isLoading: boolean
  reloadCommunityOfPractices: () => Promise<void>
}

export const useStartCommunityOfPractices = (priorityId: number, implementationPhase: boolean): UseStartCommunityOfPracticesType => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [startCommunityOfPractices, setStartCommunityOfPractices] = useState<StartCommunityOfPractice[]>([])

  const loadCommunityOfPractices = async (): Promise<void> => {
    setIsLoading(true)

    const communityOfPractices = await handleApiResponse(GetStartCommunityOfPractices(priorityId, implementationPhase))
    if (communityOfPractices != null) setStartCommunityOfPractices(communityOfPractices)

    setIsLoading(false)
  }

  useEffect(() => {
    void loadCommunityOfPractices()
  }, [])

  return { startCommunityOfPractices, isLoading, reloadCommunityOfPractices: loadCommunityOfPractices }
}

interface UseStartCommunityOfPracticeModalType {
  handleAddCommunityOfPractice: () => void
  handleEditCommunityOfPractice: (startCommunityOfPractice: StartCommunityOfPractice) => Promise<void>
  handleCloseModal: () => Promise<void>
  showCommunityOfPracticeModal: boolean
  initialValues?: UpdateStartCommunityOfPracticeFormValues
}

export const useStartCommunityOfPracticeModal = (implementationPhase: boolean): UseStartCommunityOfPracticeModalType => {
  const [initialValues, setInitialValues] = useState<UpdateStartCommunityOfPracticeFormValues>()
  const [showCommunityOfPracticeModal, setShowCommunityOfPracticeModal] = useState(false)

  const handleAddCommunityOfPractice = (): void => {
    setInitialValues(undefined)
    setShowCommunityOfPracticeModal(true)
  }

  const handleEditCommunityOfPractice = async (startCommunityOfPractice: StartCommunityOfPractice): Promise<void> => {
    setInitialValues({
      ...startCommunityOfPractice,
      trainingId: startCommunityOfPractice.startTraining.id ?? null,
      budgetRequest: startCommunityOfPractice.budgetRequest.toString().replace('$', ''),
      isImplementationPhase: implementationPhase
    })
    setShowCommunityOfPracticeModal(true)
    await LockActivity(startCommunityOfPractice.id, 'START Community Of Practice', startCommunityOfPractice.isUpdate)
  }

  const handleCloseModal = async (): Promise<void> => {
    setShowCommunityOfPracticeModal(false)
    if (initialValues != null) {
      await UnlockActivity(initialValues.id, 'START Community Of Practice', initialValues.isUpdate)
    }
  }

  return {
    handleAddCommunityOfPractice,
    handleEditCommunityOfPractice,
    handleCloseModal,
    showCommunityOfPracticeModal,
    initialValues
  }
}
